'use client';

import React, { useEffect } from 'react';
import Script from 'next/script';
import { usePathname } from 'next/navigation';
import { useAuth } from 'src/context/auth';

// Extend Window interface to include Intercom
declare global {
  interface Window {
    Intercom: any;
    intercomSettings: {
      api_base: string;
      app_id: string;
      hide_default_launcher: boolean;
    };
  }
}

const IntercomWidget: React.FC = () => {
  const pathname = usePathname();
  const { user } = useAuth();
  const appId = process.env.NEXT_PUBLIC_INTERCOM_APP_ID;

  // Handle user identification when user logs in
  useEffect(() => {
    if (!appId || !window.Intercom) return;

    if (user && !user.impersonation) {
      // Boot Intercom with authenticated user data
      window.Intercom('boot', {
        api_base: 'https://api-iam.intercom.io',
        app_id: appId,
        hide_default_launcher: true,
        user_id: user.id,
        email: user.email,
        name: `${user.first_name} ${user.last_name}`,
        username: user.username,
        company: {
          company_id: user.company.id,
          name: user.company.name,
        },
      });
    }
  }, [user, appId]);

  // Handle route changes
  useEffect(() => {
    if (!window.Intercom) return;

    // Update Intercom on route change
    window.Intercom('update');
  }, [pathname]);

  if (!appId) {
    console.error('Intercom app ID is not set. Please check NEXT_PUBLIC_INTERCOM_APP_ID environment variable.');
    return null;
  }

  return (
    <>
      <Script
        id="intercom-settings"
        strategy="lazyOnload"
        dangerouslySetInnerHTML={{
          __html: `window.intercomSettings = { app_id: "${appId}", api_base: "https://api-iam.intercom.io", hide_default_launcher: true };`,
        }}
      />
      <Script
        id="intercom-widget"
        strategy="lazyOnload"
        src={`https://widget.intercom.io/widget/${appId}`}
      />
    </>
  );
};

/**
 * Shutdown Intercom session
 * Should be called on logout to clear user data and prevent conversation history leakage
 */
export const shutdownIntercom = (): void => {
  if (typeof window !== 'undefined' && window.Intercom) {
    window.Intercom('shutdown');
  }
};

export default IntercomWidget;
